async function parseJson(response: Response) {
  return response.json().catch(() => null);
}

function extractErrorMessage(payload: any, fallback: string) {
  return payload?.error?.message || payload?.error || fallback;
}

const PROJECT_UPDATED_EVENT = "project-updated";

function emitProjectUpdated(projectId: string) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(PROJECT_UPDATED_EVENT, { detail: { projectId } }));
}

type PatchProjectOptions = {
  notifyProjectUpdated?: boolean;
};

export async function patchProject(
  projectId: string,
  data: unknown,
  options: PatchProjectOptions = {},
) {
  const response = await fetch(`/api/v1/projects/${projectId}`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  });

  const json = await parseJson(response);
  if (!response.ok) {
    throw new Error(extractErrorMessage(json, "Failed to update project"));
  }

  if (options.notifyProjectUpdated) {
    emitProjectUpdated(projectId);
  }

  return json?.data;
}

export async function getProjectById(projectId: string) {
  const response = await fetch(`/api/v1/projects/${projectId}`, {
    cache: "no-store",
  });
  const json = await parseJson(response);
  if (!response.ok) {
    throw new Error(extractErrorMessage(json, "Failed to fetch project"));
  }

  return json?.data;
}

export async function getProjectDetailsById(projectId: string) {
  const response = await fetch(`/api/v1/projects/${projectId}/details`, {
    cache: "no-store",
  });
  const json = await parseJson(response);
  if (!response.ok) {
    throw new Error(extractErrorMessage(json, "Failed to fetch project details"));
  }

  return json?.data;
}

export async function createProject(data: unknown) {
  const response = await fetch("/api/v1/projects", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  });

  const json = await parseJson(response);
  if (!response.ok) {
    throw new Error(extractErrorMessage(json, "Failed to create project"));
  }

  return json?.data;
}

export async function refreshProject(projectId: string) {
  const response = await fetch(`/api/v1/projects/${projectId}/refresh`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
  });

  const json = await parseJson(response);
  if (!response.ok) {
    throw new Error(extractErrorMessage(json, "Failed to refresh project"));
  }

  return json?.data;
}

export async function deleteProjectById(projectId: string) {
  const response = await fetch(`/api/v1/projects/${projectId}`, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
    },
  });

  const json = await parseJson(response);
  if (!response.ok) {
    throw new Error(extractErrorMessage(json, "Failed to delete project"));
  }

  return json?.data;
}

export async function getProjectFiles(projectId: string) {
  const response = await fetch(`/api/v1/projects/${projectId}/files`);

  const json = await parseJson(response);
  if (!response.ok) {
    throw new Error(extractErrorMessage(json, "Failed to fetch project files"));
  }

  return json?.data?.files || [];
}

export async function listProjects<T = unknown>(input: {
  organizationId?: string;
  includeAllOrganizations?: boolean;
  page?: number;
  pageSize?: number;
  sort?: string;
  status?: string;
  search?: string;
}) {
  const params = new URLSearchParams();
  if (input.organizationId) params.set("organizationId", input.organizationId);
  params.set("page", String(input.page ?? 1));
  params.set("pageSize", String(input.pageSize ?? 20));
  if (input.sort) params.set("sort", input.sort);
  if (input.status) params.set("status", input.status);
  if (input.search?.trim()) params.set("search", input.search.trim());
  if (input.includeAllOrganizations) params.set("includeAllOrganizations", "true");

  const response = await fetch(`/api/v1/projects?${params.toString()}`);
  const json = await parseJson(response);
  if (!response.ok) {
    throw new Error(extractErrorMessage(json, "Failed to load projects"));
  }

  return {
    data: (json?.data || []) as T[],
    pagination: json?.pagination || null,
  };
}

export async function exportAllProjects<T = unknown>() {
  const response = await fetch("/api/v1/projects/export");
  const json = await parseJson(response);
  if (!response.ok) {
    throw new Error(extractErrorMessage(json, "Failed to export project data"));
  }

  return (json?.data || []) as T[];
}
